import { ExerciseFilter } from "@domain/exercise/exercise-filter-manager";
import { FilterCategory } from "src/domain/filters/filter";
import {
  BaseExercise,
  ExerciseBodyPart,
  ExerciseCategory,
  ExerciseDifficulty,
  ExerciseStartingPosition
} from "@domain/exercise/exercise";
import { exerciseBodyParts } from "@domain/exercise/exercise";
import { I18nFormat } from "@core/i18n/i18n";
import { BodyPartIcon } from "@ui/components/exercise/body-part-icon";
import React from "react";

type FormatFunction<T> = (item: T) => string;

export const createCategoriesFilter = (
  items: ExerciseCategory[],
  label: string,
  formatExerciseCategory: FormatFunction<ExerciseCategory>
): FilterCategory<ExerciseFilter> => ({
  id: "category",
  label,
  filters: items.map((category) => ({
    id: `category-${category}`,
    label: formatExerciseCategory(category),
    predicate: (exercise: BaseExercise) => exercise.category === category,
  })),
});

export const createBodyPartsFilter = (
  items: ExerciseBodyPart[],
  label: string,
  formatExerciseBodyPart: FormatFunction<ExerciseBodyPart>
): FilterCategory<ExerciseFilter> => ({
  id: "bodyPart",
  label,
  filters: items.map((bodyPart) => ({
    id: `bodyPart-${bodyPart}`,
    label: formatExerciseBodyPart(bodyPart),
    predicate: (exercise: BaseExercise) => exerciseBodyParts(exercise).includes(bodyPart),
    icon: <BodyPartIcon bodyPart={bodyPart} width={24} height={24} />
  })),
});

export const createStartingPositionFilter = (
  items: ExerciseStartingPosition[],
  label: string,
  formatExerciseStartingPosition: FormatFunction<ExerciseStartingPosition>
): FilterCategory<ExerciseFilter> => ({
  id: "startingPosition",
  label,
  filters: items.map((startingPosition) => ({
    id: `startingPosition-${startingPosition}`,
    label: formatExerciseStartingPosition(startingPosition),
    predicate: (exercise: BaseExercise) => exercise.startingPosition === startingPosition,
  })),
});

export const createWithWheelchairFilter = (
  label: string,
  formatExerciseWithWheelchair: FormatFunction<boolean>
): FilterCategory<ExerciseFilter> => ({
  id: "withWheelchair",
  label,
  filters: [
    {
      id: "withWheelchair-true",
      label: formatExerciseWithWheelchair(true),
      predicate: (exercise: BaseExercise) => exercise.withWheelchair === true,
    },
  ],
});

export const createHelpNeededFilter = (
  label: string,
  formatExerciseHelpNeeded: FormatFunction<boolean>
): FilterCategory<ExerciseFilter> => ({
  id: "helpNeeded",
  label,
  filters: [
    {
      id: "helpNeeded-true",
      label: formatExerciseHelpNeeded(true),
      predicate: (exercise: BaseExercise) => exercise.helpNeeded === true,
    },
  ],
});

export const createDifficultyFilter = (
  items: ExerciseDifficulty[],
  label: string,
  formatExerciseDifficulty: FormatFunction<ExerciseDifficulty>
): FilterCategory<ExerciseFilter> => ({
  id: "difficulty",
  label,
  filters: items.map((difficulty) => ({
    id: `difficulty-${difficulty}`,
    label: formatExerciseDifficulty(difficulty),
    predicate: (exercise: BaseExercise) => exercise.difficulty === difficulty,
  })),
});

export const createEquipmentFilter = (
  equipmentNames: string[],
  format: I18nFormat
): FilterCategory<ExerciseFilter> => ({
  id: "equipment",
  label: format("exercise.equipment.title"),
  filters: [
    {
      id: "equipment-none",
      label: format("exercise.equipment.none"),
      predicate: (exercise: BaseExercise) =>
        exercise.optionalEquipments.length === 0 && exercise.requiredEquipments.length === 0,
    },
    ...equipmentNames.map((equipment) => ({
      id: `equipment-${equipment}`,
      label: equipment,
      predicate: (exercise: BaseExercise) =>
        exercise.optionalEquipments.some(eq => eq.name === equipment) ||
        exercise.requiredEquipments.some(eq => eq.name === equipment),
    })),
  ],
});
